import React, { useEffect, useState } from 'react';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import useIssues from '../../../hooks/useIssues';
import { Page } from '@hud/web-components/Layout/Page';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import useAnalytics from '../../../hooks/useAnalytics';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import IssuesTable from './IssuesTable';
import useRulesNames from '../../../hooks/useRulesNames';

type HeadsUpPageQueryParams = CommonTableQueryParams & {
  services: number[];
  rules: string[];
};

export default function HeadsUpPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { userPreferredServices } = useUserPreferences();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageState, setPageState, resetPageState] =
    useQueryParamState<HeadsUpPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'detectionTime',
      desc: true,

      services: [],
      rules: [],
    });
  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  useEffect(() => {
    if (pageState.services.length === 0) {
      setPageState({ services: userPreferredServices ?? [] });
    }
  }, [userPreferredServices]);

  const handleFilteredServicesChange = (value: number[]) => {
    setPageState({ services: value, page: 1 });
  };

  const handleFilteredRulesChange = (value: string[]) => {
    setPageState({ rules: value, page: 1 });
  };

  // analytics - issues_searched
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('issues_searched', {
      search: tableState.search,
      environment: environment.name,
    });
  }, [analytics, environment?.name, tableState.search]);

  // analytics - issues_sorted
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('issues_sorted', {
      column_name: tableState.orderBy.key,
      column_order: tableState.orderBy.sortOrder,
      environment: environment.name,
    });
  }, [
    analytics,
    environment?.name,
    tableState.orderBy.key,
    tableState.orderBy.sortOrder,
  ]);

  // analytics - issues_paginated
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('issues_paginated', {
      page: tableState.page,
      environment: environment.name,
    });
  }, [analytics, environment?.name, tableState.page]);

  // analytics - issues_filtered
  useEffect(() => {
    if (!environment?.name || pageState.services.length === 0) return;
    analytics.capture('issues_service_filtered', {
      environment: environment.name,
      service_ids: pageState.services,
    });
  }, [analytics, environment?.name, pageState.services]);

  const rulesNamesQuery = useRulesNames();
  const headsUpQuery = useIssues({
    timeFrame,
    environmentName: environment?.name,
  });

  const loading = headsUpQuery.isLoading || rulesNamesQuery.isLoading;
  const isError = headsUpQuery.isError || rulesNamesQuery.isError;
  const isSuccess = headsUpQuery.isSuccess && rulesNamesQuery.isSuccess;

  useEffect(() => {
    document.title = `Heads up • Hud`;
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setTotalPages(Math.ceil(headsUpQuery.data.issues.length / DEFAULT_PAGE_SIZE));
    }
  }, [headsUpQuery.data, isSuccess]);

  if (isError) {
    return (
      <Page
        header={'Heads Up'}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        <div>Failed to load Heads-Up</div>
      </Page>
    );
  }

  return (
    <Page
      header={'Heads Up'}
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <IssuesTable
        aria-busy={loading}
        issues={headsUpQuery.data?.issues || []}
        rulesNames={rulesNamesQuery.data?.rules || []}
        loading={loading}
        // pagination
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        // search
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        // sort
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        // filter
        filteredServices={pageState.services.map(Number)}
        onFilteredServicesChange={handleFilteredServicesChange}
        filteredRules={pageState.rules}
        onFilteredRulesChange={handleFilteredRulesChange}
      />
    </Page>
  );
}
